import React, { memo, useMemo, useEffect } from 'react';
import cn from 'classnames';
import useSWRInfinite from 'swr/infinite';
import queryString from 'query-string';

import { AxiosResponse } from 'axios';
import { useTranslation } from 'next-i18next';
import { Row } from 'components/layout';
import TouchpointCard from 'components/candidate/TouchpointCard';
import { Button } from 'components/form';
import { MatchCardsSkeleton } from 'components/skeletons/dashboard.component';
import { API_ROUTES } from 'lib/api-routes';
import {
  parseArrayResponse,
  parseAxiosPaginatedResponse,
  parseResponse,
} from 'lib/utils/parser';

import { TouchpointType } from 'lib/models/touchpoint';
import { CompanyAttributes } from 'lib/models/company';
import { StudentSocietyAttributes } from 'lib/models/student-society';

import {
  OpportunitiesResponse,
  OpportunityAttributes,
} from 'lib/models/opportunity';
import { apiInstance } from 'lib/utils/axios';
import { JobTouchpointable } from 'lib/models/job';
import { InternshipTouchpointable } from 'lib/models/internship';
import { getOpportunityPublicPagePathname } from 'utils/urls';

import styles from '../Section.module.scss';

const PER_PAGE_COUNT = 5;

export interface FeaturedOpportunitiesProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const FeaturedOpportunities: React.FC<FeaturedOpportunitiesProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: FeaturedOpportunitiesProps) => {
    const endOfPageRef = React.useRef<HTMLDivElement | null>(null);
    const scrollToBottom = () => {
      endOfPageRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    };
    const [t] = useTranslation('candidate-dashboard');
    const getTouchpointListKey = (pageIndex: number) => {
      const params = {
        featured: true,
        page: pageIndex + 1,
        per_page: PER_PAGE_COUNT,
        with_details: true,
      };
      const qs = queryString.stringify(params, {
        arrayFormat: 'bracket',
        skipEmptyString: true,
      });

      return `${API_ROUTES.CAMPAIGNS}?${qs}`;
    };

    const {
      data: touchpointsResponse,
      error: touchpointsError,
      size,
      setSize,
    } = useSWRInfinite<AxiosResponse<OpportunitiesResponse>>(
      getTouchpointListKey,
      apiInstance.get,
      {
        revalidateOnFocus: false,
        revalidateFirstPage: false,
      }
    );

    const isAPILoading = !touchpointsResponse && !touchpointsError;
    const isLoadingMore =
      isAPILoading ||
      (size > 0 &&
        touchpointsResponse &&
        typeof touchpointsResponse[size - 1] === 'undefined');

    const touchpoints = useMemo(() => {
      return parseAxiosPaginatedResponse<OpportunityAttributes>(
        touchpointsResponse
      );
    }, [touchpointsResponse]);

    const totalCount = touchpointsResponse
      ? touchpointsResponse[0]?.data?.meta?.total
      : undefined;

    const hasMoreRecords = useMemo(() => {
      const firstApplicationList = Array.isArray(touchpointsResponse)
        ? touchpointsResponse[0]
        : null;
      if (firstApplicationList) {
        return (
          (firstApplicationList?.data?.meta?.total || 0) > size * PER_PAGE_COUNT
        );
      } else {
        return false;
      }
    }, [touchpointsResponse, size]);

    useEffect(() => {
      if (!isLoadingMore && size > 1) {
        scrollToBottom();
      }
    }, [isLoadingMore, size]);

    useEffect(() => {
      setSize(1);

      return () => {
        endOfPageRef.current = null;
        setSize(1);
      };
    }, []);

    const getTouchpointCard = (touchpoints: Array<OpportunityAttributes>) => {
      return (
        <div className={styles.touchpointcardList}>
          {touchpoints?.map(
            ({
              id,
              title,
              start_date,
              creatable_for,
              touchpointable_type,
              touchpointable,
              application_deadline,
              apply_url,
              pay_currency,
              kind,
              slug,
              cities,
              cohost_applications: cohostApplicationsResponse,
              is_eligible_for_auto_apply,
            }) => {
              const createdBy = parseResponse<
                CompanyAttributes | StudentSocietyAttributes
              >(creatable_for);
              const { name, icon_url, is_verified, huzzle_verified } =
                createdBy;

              const publicPageUrl = `${
                window.location.origin
              }${getOpportunityPublicPagePathname(touchpointable_type, slug)}`;
              const cohost_applications = parseArrayResponse(
                cohostApplicationsResponse
              );
              const { maximum_salary, minimum_salary } = parseResponse(
                touchpointable as JobTouchpointable | InternshipTouchpointable
              );
              return (
                <TouchpointCard
                  key={id}
                  icon_url={icon_url}
                  title={title}
                  createdBy={name}
                  startDate={start_date}
                  created_at={''}
                  application_deadline={application_deadline}
                  is_verified={is_verified}
                  touchpointable_type={touchpointable_type}
                  maximum_salary={maximum_salary}
                  minimum_salary={minimum_salary}
                  pay_currency={pay_currency || 'GBP'}
                  isExpired={false}
                  url={publicPageUrl}
                  className={styles.touchpointcard}
                  isEasyApply={
                    touchpointable_type !== TouchpointType.Event && !apply_url
                  }
                  isNewSet={false}
                  huzzle_verified={huzzle_verified}
                  kind={kind}
                  isOnboarding
                  cities={cities}
                  id={id}
                  slug={slug}
                  cohost_applications={cohost_applications}
                  following={null}
                  apply_url={null}
                  extension_installed={true}
                  showManagerProgress={false}
                  is_eligible_for_auto_apply={is_eligible_for_auto_apply}
                />
              );
            }
          )}
        </div>
      );
    };

    const contentClassNames = cn(
      {
        [styles.container]: true,
        [styles.section]: totalCount !== 0,
        [styles.hide]: totalCount === 0,
      },
      className
    );

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="PastMatches"
        ref={endOfPageRef}
      >
        <div className={styles.titleContainer}>
          <p className={styles.title}>{t('featured-opportunity')}</p>
        </div>
        {totalCount ? getTouchpointCard(touchpoints) : null}
        {totalCount === undefined || isLoadingMore ? (
          <MatchCardsSkeleton />
        ) : null}
        {totalCount !== PER_PAGE_COUNT ? (
          <Row align="center" columnGap={8} className={styles.countsRow}>
            <p>
              {t('featured-opportunities.showing-count', {
                length: touchpoints?.length,
                totalCount: totalCount,
              })}
            </p>
            {hasMoreRecords ? (
              <Button
                variant="text"
                color="blue"
                onClick={() => {
                  setSize(size + 1);
                }}
                className={styles.countsButton}
              >
                Show more
              </Button>
            ) : null}
          </Row>
        ) : null}
      </div>
    );
  }
);

FeaturedOpportunities.displayName = 'FeaturedMatches';
